/**
 * The audio service the whole app uses.
 *
 * It plays single phrases or whole sequences ("Read Everything": question →
 * pause → answer A → pause → answer B …), and exposes a snapshot React can
 * subscribe to.
 *
 * Arabic can come from two places, and the choice is made per phrase:
 *
 *   1. The device's own Arabic voice, through the browser. Best quality when it
 *      exists — but most Windows PCs, and plenty of phones, have none.
 *   2. The recordings that ship with the app (see clipEngine.ts). Always there,
 *      work offline, need nothing installed.
 *
 * Language rule, unchanged: if neither source can say something in the language
 * being asked for, NOTHING is spoken and `hasArabicVoice` / `hasEnglishVoice`
 * turn false so the screen can say so. A voice of the wrong language is never
 * substituted.
 */
import type { SpeechSpeed, TtsEngineId } from '../../types';
import { BUNDLED_VOICE_ID, ClipEngine } from './clipEngine';
import { PiperEngine } from './piperEngine';
import type { ArabicSource, SpeechEngine, SpeechItem, SpeechLang, TtsSnapshot, TtsVoice } from './types';
import { WebSpeechEngine } from './webSpeechEngine';

export const SPEECH_RATES: Record<SpeechSpeed, number> = {
  slow: 0.6,
  default: 0.8, // slightly slower than normal — the app's default
  normal: 1,
};

/** Silence between the question and each answer in "Read Everything". */
export const DEFAULT_GAP_MS = 750;

export class TtsController {
  private engine: SpeechEngine;
  private webEngine = new WebSpeechEngine();
  private piperEngine: PiperEngine | null = null;
  private clipEngine = new ClipEngine();

  private listeners = new Set<() => void>();
  private unsubscribeVoices: (() => void) | null = null;
  private snapshot: TtsSnapshot;

  private abort: AbortController | null = null;
  private generation = 0;
  private paused = false;
  private lastSequence: SpeechItem[] = [];
  /** Whichever engine is mid-phrase, so pause/stop reach the right one. */
  private activeEngine: SpeechEngine | null = null;

  private rate = SPEECH_RATES.default;
  private arabicVoiceId: string | null = null;
  private englishVoiceId: string | null = null;

  constructor() {
    this.engine = this.webEngine;
    this.snapshot = this.buildSnapshot('idle', null, false, null);
    if (typeof window !== 'undefined') {
      // Never leave speech running after the page goes away.
      window.addEventListener('pagehide', () => this.stop());
    }
    // The bundled recordings never go away, so this subscription is for the
    // life of the app and is never unsubscribed.
    this.clipEngine.onVoicesChanged(() => this.refreshVoices());
    void this.clipEngine.init().then(() => this.refreshVoices());
  }

  /** The recordings that ship with the app, for the Settings screen. */
  get bundled(): ClipEngine {
    return this.clipEngine;
  }

  /* ---------------- subscription ---------------- */

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getSnapshot = (): TtsSnapshot => this.snapshot;

  private notify(): void {
    this.listeners.forEach((l) => l());
  }

  private buildSnapshot(
    state: TtsSnapshot['state'],
    currentItemId: string | null,
    ready: boolean,
    error: string | null,
  ): TtsSnapshot {
    const deviceVoices = this.engine.getVoices();
    // The bundled voice is offered in the same list as the device voices, so
    // the Settings picker needs no special case: choosing it is choosing a voice.
    const voices = [...this.clipEngine.getVoices(), ...deviceVoices];
    const arabicVoices = voices.filter((v) => v.isArabic);
    const englishVoices = voices.filter((v) => v.isEnglish);
    const hasDeviceArabicVoice = deviceVoices.some((v) => v.isArabic);

    return {
      state,
      currentItemId,
      engineId: this.engine.id,
      ready,
      voices,
      arabicVoices,
      englishVoices,
      hasArabicVoice: arabicVoices.length > 0,
      hasEnglishVoice: englishVoices.length > 0,
      supportsPause: this.engine.supportsPause,
      error,
      arabicSource: this.resolveArabicSource(hasDeviceArabicVoice),
      hasDeviceArabicVoice,
      bundledReady: this.clipEngine.ready,
      bundledStats: this.clipEngine.stats,
    };
  }

  /**
   * Which Arabic voice wins.
   *
   * With no explicit choice the device voice is preferred when it exists — a
   * real system voice (Apple's Maged, Microsoft's Hamed, Google's Arabic) is
   * better than any recording we can ship — and the bundled recordings cover
   * the very common case where there is no system voice at all.
   */
  private resolveArabicSource(hasDeviceArabicVoice: boolean): ArabicSource {
    if (this.arabicVoiceId === BUNDLED_VOICE_ID) {
      return this.clipEngine.ready ? 'bundled' : hasDeviceArabicVoice ? 'device' : 'none';
    }
    if (hasDeviceArabicVoice) return 'device';
    if (this.clipEngine.ready) return 'bundled';
    return 'none';
  }

  private update(
    patch: Partial<Pick<TtsSnapshot, 'state' | 'currentItemId' | 'ready' | 'error'>> = {},
  ): void {
    const prev = this.snapshot;
    this.snapshot = this.buildSnapshot(
      patch.state ?? prev.state,
      patch.currentItemId !== undefined ? patch.currentItemId : prev.currentItemId,
      patch.ready ?? prev.ready,
      patch.error !== undefined ? patch.error : prev.error,
    );
    this.notify();
  }

  private refreshVoices(): void {
    const error = this.piperEngine && this.engine === this.piperEngine ? this.piperEngine.lastError : null;
    this.snapshot = this.buildSnapshot(this.snapshot.state, this.snapshot.currentItemId, true, error);
    this.notify();
  }

  /* ---------------- configuration ---------------- */

  setSpeed(speed: SpeechSpeed): void {
    this.rate = SPEECH_RATES[speed] ?? SPEECH_RATES.default;
  }

  setVoices(arabicVoiceId: string | null, englishVoiceId: string | null): void {
    const changed = this.arabicVoiceId !== arabicVoiceId;
    this.arabicVoiceId = arabicVoiceId;
    this.englishVoiceId = englishVoiceId;
    // Choosing the bundled voice changes `arabicSource`, which the Settings
    // screen shows, so the snapshot has to be rebuilt — not just remembered.
    if (changed) this.refreshVoices();
  }

  async setEngine(engineId: TtsEngineId, piperUrl: string): Promise<void> {
    this.stop();
    if (engineId === 'piper') {
      if (!this.piperEngine) this.piperEngine = new PiperEngine(piperUrl);
      else this.piperEngine.setBaseUrl(piperUrl);
      this.engine = this.piperEngine;
    } else {
      this.engine = this.webEngine;
    }
    this.unsubscribeVoices?.();
    this.unsubscribeVoices = this.engine.onVoicesChanged(() => this.refreshVoices());
    await Promise.all([this.engine.init(), this.clipEngine.init()]);
    this.refreshVoices();
  }

  /** True when something can say this language. */
  canSpeak(lang: SpeechLang): boolean {
    return lang === 'ar' ? this.snapshot.hasArabicVoice : this.snapshot.hasEnglishVoice;
  }

  /**
   * True when this exact phrase can be said.
   *
   * Stricter than canSpeak: with no device voice, only phrases that were
   * recorded can be spoken, and a 🔊 button for anything else should be visibly
   * disabled rather than doing nothing when pressed.
   */
  canSpeakText(text: string, lang: SpeechLang): boolean {
    return this.engineFor({ id: 'probe', text, lang }) !== null;
  }

  /**
   * Picks where one phrase comes from, or null if nothing can say it.
   *
   * The bundled recordings only cover the phrases that were in the bank when
   * `npm run audio` last ran, so a question added later falls through to the
   * device voice rather than going quiet.
   */
  private engineFor(item: SpeechItem): SpeechEngine | null {
    if (item.lang === 'en') return this.snapshot.hasEnglishVoice ? this.engine : null;

    const source = this.snapshot.arabicSource;
    if (source === 'bundled') {
      if (this.clipEngine.has(item.text)) return this.clipEngine;
      return this.snapshot.hasDeviceArabicVoice ? this.engine : null;
    }
    if (source === 'device') return this.engine;
    return null;
  }

  voicesFor(lang: SpeechLang): TtsVoice[] {
    return lang === 'ar' ? this.snapshot.arabicVoices : this.snapshot.englishVoices;
  }

  /* ---------------- playback ---------------- */

  /** Speak one phrase. Anything already playing is stopped first. */
  speak(text: string, lang: SpeechLang, id = 'single'): void {
    void this.speakSequence([{ id, text, lang }]);
  }

  /**
   * Speak several phrases in order, with a short silence between them.
   * Returns when the whole sequence has finished (or was stopped).
   */
  async speakSequence(items: SpeechItem[]): Promise<void> {
    this.stop();
    const speakable = items
      .map((item) => ({ item, engine: this.engineFor(item) }))
      .filter((x): x is { item: SpeechItem; engine: SpeechEngine } =>
        x.item.text.trim().length > 0 && x.engine !== null,
      );
    if (speakable.length === 0) {
      this.update({ state: 'idle', currentItemId: null });
      return;
    }

    this.lastSequence = items;
    const generation = ++this.generation;
    const controller = new AbortController();
    this.abort = controller;
    this.paused = false;
    this.update({ state: 'speaking', currentItemId: speakable[0].item.id, error: null });

    for (let i = 0; i < speakable.length; i += 1) {
      const { item, engine } = speakable[i];
      if (generation !== this.generation || controller.signal.aborted) return;

      this.update({ currentItemId: item.id });
      this.activeEngine = engine;
      // The bundled voice has no notion of "which voice", so a device voice id
      // is only meaningful to the device engine.
      const voiceId =
        engine === this.clipEngine
          ? null
          : item.lang === 'ar'
            ? this.arabicVoiceId
            : this.englishVoiceId;
      await engine.speak(item.text, {
        lang: item.lang,
        rate: this.rate,
        voiceId,
        signal: controller.signal,
      });

      if (generation !== this.generation || controller.signal.aborted) return;

      const gap = item.gapAfterMs ?? (i < speakable.length - 1 ? DEFAULT_GAP_MS : 0);
      if (gap > 0) {
        const finished = await this.wait(gap, generation, controller.signal);
        if (!finished) return;
      }
    }

    if (generation === this.generation) {
      this.abort = null;
      this.activeEngine = null;
      this.update({ state: 'idle', currentItemId: null });
    }
  }

  /** Sleep that honours pause and stop. Returns false if it was interrupted. */
  private wait(ms: number, generation: number, signal: AbortSignal): Promise<boolean> {
    return new Promise((resolve) => {
      let elapsed = 0;
      const step = 100;
      const timer = window.setInterval(() => {
        if (generation !== this.generation || signal.aborted) {
          window.clearInterval(timer);
          resolve(false);
          return;
        }
        if (this.paused) return; // hold the gap while paused
        elapsed += step;
        if (elapsed >= ms) {
          window.clearInterval(timer);
          resolve(true);
        }
      }, step);
    });
  }

  pause(): void {
    if (this.snapshot.state !== 'speaking') return;
    this.paused = true;
    (this.activeEngine ?? this.engine).pause();
    this.update({ state: 'paused' });
  }

  resume(): void {
    if (this.snapshot.state !== 'paused') return;
    this.paused = false;
    (this.activeEngine ?? this.engine).resume();
    this.update({ state: 'speaking' });
  }

  stop(): void {
    this.generation += 1;
    this.paused = false;
    this.abort?.abort();
    this.abort = null;
    // Both, unconditionally: a sequence can switch engines part-way through,
    // and stopping only the current one would leave the other still playing.
    this.engine.cancel();
    this.clipEngine.cancel();
    this.activeEngine = null;
    if (this.snapshot.state !== 'idle' || this.snapshot.currentItemId !== null) {
      this.update({ state: 'idle', currentItemId: null });
    }
  }

  /** Play the last sequence again from the beginning. */
  replay(): void {
    if (this.lastSequence.length === 0) return;
    void this.speakSequence(this.lastSequence);
  }

  hasSequence(): boolean {
    return this.lastSequence.length > 0;
  }
}

/** One shared instance for the whole app. */
export const ttsController = new TtsController();
